import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  IconEye,
  IconCalendar,
  IconUsers,
  IconShip,
} from '@tabler/icons-react';
import BookingStatusBadge from './BookingStatusBadge';
import type { PartnerBooking } from '../../../../types/partner';

interface BookingCardProps {
  booking: PartnerBooking;
}

const BookingCard: FC<BookingCardProps> = ({ booking }) => {
  const navigate = useNavigate();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Booking #{booking.bookingId}
            </h3>
            <BookingStatusBadge status={booking.bookingStatus} />
          </div>

          <div className="space-y-2 text-sm text-gray-600">
            {booking.leadGuestFullName && (
              <div className="mb-2 rounded-md bg-gray-50 p-2">
                <div className="font-medium text-gray-900">
                  {booking.leadGuestFullName}
                </div>
                {booking.leadGuestEmail && (
                  <div className="text-xs text-gray-500">
                    {booking.leadGuestEmail}
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              <IconShip className="h-4 w-4 text-gray-400" />
              <span className="font-medium">{booking.boatName}</span>
            </div>

            {booking.routeName && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400">Route:</span>
                <span>{booking.routeName}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <IconCalendar className="h-4 w-4 text-gray-400" />
              <span>
                {format(new Date(booking.embarkationDate), 'MMM dd, yyyy')} -{' '}
                {format(new Date(booking.disembarkationDate), 'MMM dd, yyyy')}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <IconUsers className="h-4 w-4 text-gray-400" />
              <span>{booking.guestCount} guest(s)</span>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate(`/partner/bookings/${booking.bookingId}`)}
          className="ml-4 flex items-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          <IconEye className="h-4 w-4" />
          View Details
        </button>
      </div>
    </div>
  );
};

export default BookingCard;
